import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResourceType } from '@cotera/api';
import { resourceKind } from '@cotera/sdk/core';
import type { MentionClickData } from '@cotera/client/v0/components/ai-elements/readonly-editor-state';
import {
  parseResourceMention,
  parseWorkflowToolMention,
} from '@cotera/client/v0/chat/artifacts';
import { useWhoami } from '../stores/org';

/**
 * When the user clicks a workspace resource @ mention (or a workflow-tool
 * mention), navigate to that resource's v1 page.
 * Returns whether navigation was performed (caller can then skip tool handling).
 */
export function useNavigateOnResourceMentionClick(): (
  data: MentionClickData
) => boolean {
  const navigate = useNavigate();
  const orgSlug = useWhoami((s) => s.org.slug);

  return useCallback(
    (data: MentionClickData) => {
      // Workflow mentions are tool keys; open the workflow page the same way
      // other resource mentions navigate to their item route.
      const workflowId = parseWorkflowToolMention(data);
      if (workflowId !== null) {
        const { itemRouteSegment } = resourceKind(ResourceType.Workflow);
        navigate(`/${orgSlug}/${itemRouteSegment}/${workflowId}`);
        return true;
      }

      const resource = parseResourceMention(data);
      if (resource === null) {
        return false;
      }
      const { itemRouteSegment } = resourceKind(resource.resourceType);
      navigate(`/${orgSlug}/${itemRouteSegment}/${resource.resourceId}`);
      return true;
    },
    [navigate, orgSlug]
  );
}
